import React, { useEffect, useRef } from "react";

// Paints a login overlay's backdrop with a <video> instead of a CSS colour, so
// Clarity recordings show the game behind the popup.
//
// The stream's Clarity time-lapse works because Clarity can't record <video>
// content: in replay the video is blank, so the snapshot <img> behind it shows
// through (see .arcware-clarity-bg). The login overlay's opaque CSS background
// hides that snapshot in replay just as it hides the game live. So we draw the
// same solid colour into a MediaStream and play it in a <video>: the user sees an
// identical backdrop, and Clarity replays a blank video with the game frame under it.
//
// onReady fires once the video is actually showing the colour; only then does the
// parent drop its CSS background, so the user never glimpses the game. If any step
// fails (no captureStream, autoplay refused) onReady never fires and the plain CSS
// background stays — no visible change, just no see-through in Clarity.
const ClaritySeeThroughBackdrop = ({ color, onReady }) => {
  const videoRef = useRef(null);
  const onReadyRef = useRef(onReady);
  useEffect(() => { onReadyRef.current = onReady; }, [onReady]);

  useEffect(() => {
    const video = videoRef.current;
    let stream = null;
    let done = false;

    const paint = (canvas) => {
      const ctx = canvas.getContext("2d");
      ctx.fillStyle = color;
      ctx.fillRect(0, 0, canvas.width, canvas.height);
    };

    const markReady = () => {
      if (done || !video || video.paused || !video.videoWidth) return;
      done = true;
      onReadyRef.current?.();
    };

    try {
      // Never attached to the DOM, so Clarity has nothing to record for it.
      const canvas = document.createElement("canvas");
      canvas.width = 16;
      canvas.height = 16;
      paint(canvas);
      stream = canvas.captureStream?.();
      if (!stream || !video) return undefined;
      // Chrome only emits a frame when the canvas changes after capture starts.
      paint(canvas);
      try { stream.getVideoTracks()[0]?.requestFrame?.(); } catch (_) {}

      video.addEventListener("playing", markReady);
      video.addEventListener("resize", markReady);
      video.srcObject = stream;
      video.play?.()?.catch?.(() => {});
    } catch (_) {
      return undefined;
    }

    return () => {
      video.removeEventListener("playing", markReady);
      video.removeEventListener("resize", markReady);
      try { video.srcObject = null; } catch (_) {}
      try { stream?.getTracks().forEach((t) => t.stop()); } catch (_) {}
    };
  }, [color]);

  return (
    <video
      ref={videoRef}
      autoPlay
      muted
      playsInline
      aria-hidden="true"
      style={{
        position: "absolute",
        inset: 0,
        width: "100%",
        height: "100%",
        objectFit: "fill",
        zIndex: 0,
        pointerEvents: "none",
        background: "transparent",
      }}
    />
  );
};

export default ClaritySeeThroughBackdrop;
